import classNames from 'classnames';
import { useAuth0User } from '@hooks/useAuth0User';
import Button from '@components/atoms/button';
import Icon from '@components/atoms/icon';
import Tooltip from '@components/atoms/tooltip';
import {
  BodyRegularBold,
  BodyRegular,
  H2,
  CaptionRegular
} from '@components/atoms/typography';
import { JoinTableType } from '@lib/page-utils/Join';
import {
  CleverTap,
  ClevertapEvents,
  ClevertapEventProperties
} from '@lib/clevertap';
import {
  getCheckoutPath,
  getCleverTapMembershipTier,
  getCleverTapTierName,
  isPaidMember
} from '@lib/utils';
import {
  AnalyticsEvents,
  captureAnalyticsEvent,
  toAnalyticsMembershipTier
} from '@lib/analytics/events';
import AnimateSpinner from '@components/animate-spinner';

// FFH-2372: analytics placement for the join-page comparison table CTA.
const JOIN_TABLE_PLACEMENT = 'join_table';

export type JoinTableRow = {
  text: string;
  type: 'divider' | 'feature';
  explainer?: string;
  starter?: string | boolean;
  pro?: string | boolean;
  ultra?: string | boolean;
  backgroundColor?: boolean;
  isLast?: boolean;
  isAnnual?: boolean;
  starterText?: string;
  proText?: string;
  ultraText?: string;
};

const TableRow = ({
  text,
  type,
  explainer,
  starter,
  pro,
  ultra,
  backgroundColor,
  isLast,
  isAnnual,
  starterText,
  proText,
  ultraText
}: JoinTableRow) => {
  return (
    <tr
      className={classNames(
        backgroundColor ? 'bg-primary-100' : '',
        type === 'divider' ? 'mt-3' : ''
      )}
    >
      {type === 'divider' ? (
        <>
          <td className="w-4/12 flex py-3 px-6 items-center">
            <BodyRegularBold className="whitespace-nowrap">
              {text}
            </BodyRegularBold>
          </td>
          <td></td>
          <td
            className={classNames(
              'border-l-[3px] border-r-[3px] border-warning-600',
              !isAnnual ? 'border-r-[3px]' : ''
            )}
          ></td>
          <td></td>
        </>
      ) : (
        <>
          <td className="w-4/12">
            <div className="flex py-3 px-6 items-center">
              <BodyRegular>{text}</BodyRegular>
              {explainer && (
                <Tooltip variant="dark" title={explainer}>
                  <span className="mb-1 ml-2">
                    <Icon
                      className="text-neutral-600 hover:text-primary-600"
                      name="InfoFilled"
                    />
                  </span>
                </Tooltip>
              )}
            </div>
          </td>
          <td>
            <div className="flex justify-center items-center">
              {starter ? (
                <div
                  className={`flex flex-col items-center justify-center ${
                    starterText ? 'py-2' : ''
                  }`}
                >
                  <Icon
                    className="text-primary rounded-full w-6 h-6"
                    name="CheckCircle"
                  />
                  {starterText && (
                    <CaptionRegular className="pt-1">
                      {starterText}
                    </CaptionRegular>
                  )}
                </div>
              ) : (
                '-'
              )}
            </div>
          </td>
          <td
            className={classNames(
              'relative',
              !isAnnual
                ? 'border-l-[3px] border-r-[3px] border-warning-600'
                : '',
              isAnnual ? 'border-l-[3px] border-warning-600' : ''
            )}
          >
            <div className="flex justify-center items-center">
              {typeof pro === 'string' ? (
                <BodyRegular>{pro}</BodyRegular>
              ) : pro ? (
                <div
                  className={`flex flex-col items-center justify-center ${
                    proText ? 'py-2' : ''
                  }`}
                >
                  <Icon
                    className="text-primary rounded-full w-6 h-6"
                    name="CheckCircle"
                  />
                  {proText && (
                    <CaptionRegular className="pt-1">{proText}</CaptionRegular>
                  )}
                </div>
              ) : (
                '-'
              )}
            </div>
            {isLast && (
              <div
                className={`absolute w-[calc(100%+6px)] bottom-[-28px] left-[-3px] right-0 ${
                  pro ? 'rounded-bl-xl' : 'rounded-b-xl border-r-[3px]'
                } border-b-[3px] border-l-[3px] border-warning-600 h-8`}
              ></div>
            )}
          </td>
          <td
            className={classNames(
              'relative',
              isAnnual ? 'border-l-[3px] border-warning-600' : ''
            )}
          >
            <div className="flex justify-center items-center">
              {typeof ultra === 'string' ? (
                <BodyRegular>{ultra}</BodyRegular>
              ) : ultra ? (
                <div
                  className={`flex flex-col items-center justify-center ${
                    ultraText ? 'py-2' : ''
                  }`}
                >
                  <Icon
                    className="text-primary rounded-full w-6 h-6"
                    name="CheckCircle"
                  />
                  {ultraText && (
                    <CaptionRegular className="pt-1">
                      {ultraText}
                    </CaptionRegular>
                  )}
                </div>
              ) : (
                '-'
              )}
            </div>
            {isLast && (
              <div
                className={`absolute w-[calc(100%+6px)] bottom-[-28px] left-[-3px] right-0 ${
                  pro ? 'rounded-br-xl border-b-[3px] border-r-[3px]' : ''
                }  border-warning-600 h-8`}
              ></div>
            )}
          </td>
        </>
      )}
    </tr>
  );
};

const JoinTable = ({
  tableHeaders,
  rows,
  userMembershipIds,
  variant,
  plansAreLoading,
  showFullPriceOnly = false
}: JoinTableType & { showFullPriceOnly?: boolean }) => {
  const { user } = useAuth0User();

  return (
    <div className="relative">
      <table className="relative w-full">
        <thead>
          <tr>
            {tableHeaders.map(
              (
                {
                  header,
                  subHeader,
                  yearlyId,
                  discountedAnnualPerMonthPrice,
                  fullAnnualPerMonthPrice
                },
                index
              ) => {
                const isPro = header === 'Pro';
                const displayPriceDiscounted = `${discountedAnnualPerMonthPrice}/mth`;
                const priceId = yearlyId;
                const hideCTAButton =
                  priceId && userMembershipIds?.includes(priceId);
                const displayPriceFull = `${fullAnnualPerMonthPrice}/mth`;

                return (
                  <th
                    className={classNames(
                      isPro
                        ? 'border-l-[3px] border-r-[3px] border-warning-600'
                        : '',
                      'relative'
                    )}
                    key={`${header}-${index}`}
                  >
                    {isPro && (
                      <>
                        <div className="absolute whitespace-nowrap p-2 top-[-50px] left-1/2 -translate-x-1/2 bg-warning-600 rounded-t-lg z-10">
                          <BodyRegular className="text-white">
                            Most Popular
                          </BodyRegular>
                        </div>
                        <div className="absolute w-[calc(100%+6px)] top-[-12px] left-[-3px] right-0 rounded-t-xl border-t-[3px] border-l-[3px] border-r-[3px] border-warning-600 h-4"></div>
                      </>
                    )}
                    <BodyRegularBold className="pt-8">{header}</BodyRegularBold>
                    {/* Struck-through full price, only while a discount is
                        applied: with a 0% effective discount both prices are
                        the same string, so the strike-through would just
                        duplicate the price. */}
                    {!plansAreLoading &&
                      header &&
                      !showFullPriceOnly &&
                      fullAnnualPerMonthPrice &&
                      fullAnnualPerMonthPrice !==
                        discountedAnnualPerMonthPrice && (
                        <h2
                          className={
                            'my-3 mb-1 text-neutral-600 text-center font-normal text-3xl font-sans line-through'
                          }
                        >
                          {displayPriceFull}
                        </h2>
                      )}
                    {!plansAreLoading && header && !showFullPriceOnly && (
                      <H2 className="my-3 mb-1 text-warning-600 text-center">
                        {displayPriceDiscounted}
                      </H2>
                    )}
                    {!plansAreLoading && header && showFullPriceOnly && (
                      <H2 className="my-3 mb-1 text-center text-warning-600 ">
                        {displayPriceFull}
                      </H2>
                    )}
                    {plansAreLoading && header && (
                      <div className="flex justify-center my-2">
                        <AnimateSpinner customClasses="h-5 w-5" />
                      </div>
                    )}
                    {header && (
                      <BodyRegular className="text-warning-600 mb-3">
                        billed yearly
                      </BodyRegular>
                    )}
                    <BodyRegular className="mb-6">{subHeader}</BodyRegular>
                    {hideCTAButton && (
                      <div className="flex justify-center">
                        <p
                          className="w-[120px] text-sm font-bold text-center text-gray-500"
                          data-cy={`join-current-plan-${header.toLowerCase()}`}
                        >
                          You already have this plan
                        </p>
                      </div>
                    )}
                    {hideCTAButton
                      ? null
                      : header && (
                          <Button
                            href={getCheckoutPath(user, yearlyId)}
                            isLink
                            variant="primary"
                            onClick={() => {
                              // checkout_started: clear purchase intent,
                              // fired before Stripe takes over (FFH-1049).
                              CleverTap.event(ClevertapEvents.checkoutStarted, {
                                [ClevertapEventProperties.intendedTier]:
                                  getCleverTapTierName(header),
                                [ClevertapEventProperties.membershipTier]:
                                  getCleverTapMembershipTier(user)
                              });
                              // FFH-991 (Option A): Meta/TikTok AddToCart
                              // moved off this CTA to the checkout-page mount
                              // (GTM container, off the dataLayer push,
                              // deduped with the server CAPI). Direct calls
                              // removed.
                              // FFH-2372: PostHog alongside CleverTap. The
                              // table sells yearly plans (yearlyId) only,
                              // and no coupon is in scope here.
                              captureAnalyticsEvent(
                                AnalyticsEvents.upgradeCtaClicked,
                                {
                                  placement: JOIN_TABLE_PLACEMENT,
                                  intended_tier:
                                    toAnalyticsMembershipTier(header)
                                }
                              );
                              captureAnalyticsEvent(
                                AnalyticsEvents.checkoutStarted,
                                {
                                  intended_tier:
                                    toAnalyticsMembershipTier(header),
                                  billing_period: 'annual',
                                  coupon: null
                                }
                              );
                            }}
                          >
                            {variant === 'member-upgrade' || isPaidMember(user)
                              ? 'Change Plan'
                              : 'Get started'}
                          </Button>
                        )}
                  </th>
                );
              }
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map(
            (
              {
                text,
                type,
                explainer,
                pro,
                starter,
                ultra,
                backgroundColor,
                starterText,
                proText,
                ultraText
              },
              index
            ) => (
              <TableRow
                key={text}
                type={type}
                text={text}
                pro={pro}
                ultra={ultra}
                starter={starter}
                explainer={explainer}
                backgroundColor={backgroundColor}
                isLast={index === rows.length - 1}
                starterText={starterText}
                proText={proText}
                ultraText={ultraText}
              />
            )
          )}
        </tbody>
      </table>
    </div>
  );
};

export default JoinTable;
